import { useCallback } from 'react'

import { CheckIcon } from 'src/shared/components/icons/toasts/check-icon'
import { InfoIcon } from 'src/shared/components/icons/toasts/info-icon'
import { WarningIcon } from 'src/shared/components/icons/toasts/warning-icon'
import { XIcon } from 'src/shared/components/icons/toasts/x-icon'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '.'
import type { ToastProps } from './index'

interface LeftIconProps {
  variant: ToastProps['variant']
}

function LeftIcon({ variant }: LeftIconProps) {
  const renderIcon = useCallback(() => {
    switch (variant) {
      case 'destructive':
        return <XIcon className="text-[#D62828]" />
      case 'light':
        return <InfoIcon className="text-[#FFC300]" />
      case 'warning':
        return <WarningIcon className="text-[#FF8800]" />
      case 'default':
        return <CheckIcon className="text-secondary" />
      default:
        return <CheckIcon className="text-secondary" />
    }
  }, [variant])

  return (
    <div
      className={cn(
        'flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-[#0BA95E] bg-opacity-20',
        variant === 'destructive' && 'bg-[#D62828] bg-opacity-20',
        variant === 'light' && 'bg-[#FFC300] bg-opacity-20',
        variant === 'warning' && 'bg-[#FF8800] bg-opacity-20',
      )}
    >
      {renderIcon()}
    </div>
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="flex flex-row items-center gap-[10px]">
              <LeftIcon variant={props.variant} />
              <div className="grid gap-[2px]">
                {title && (
                  <ToastTitle className="line-clamp-2 text-[14px] font-semibold leading-[1.35] text-primary">
                    {title}
                  </ToastTitle>
                )}
                {description && (
                  <ToastDescription className="line-clamp-6 text-[12px] font-medium leading-[1.35] text-muted">
                    {description}
                  </ToastDescription>
                )}
              </div>
            </div>
            {action}
            <ToastClose className="text-primary opacity-100 group-hover:opacity-100" />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
